// Tipos, etiquetas y piezas compartidas de Automatizaciones (lista, editor y detalle).
import type { JSX } from "react";
import {
  Bell, Briefcase, CheckSquare, Clock, GitBranch, ListPlus, Mail, MessageCircle, type LucideIcon, Star, Tag, Tags, UserCog, Flag,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../lib/api.ts";

export type TriggerType = "form_submit" | "fb_lead" | "survey_response" | "list_added" | "tag_added" | "email_click";
export type StepType =
  | "send_email" | "wait" | "add_tag" | "remove_tag" | "add_to_list" | "create_deal" | "create_task"
  | "notify" | "add_score" | "set_status" | "set_owner" | "condition" | "send_whatsapp";

export type StepConfig = Record<string, string | number | boolean | undefined>;
export interface Step { id: string; type: StepType; config: StepConfig }
export interface Trigger { type: TriggerType; refId: string }
export interface Goal { type: "none" | "deal_won" | "status" | "tag"; value: string }
export interface FlowSettings {
  oncePerContact: boolean; singleFlow: boolean; goal: Goal; stopOnGoal: boolean; stopOnMeeting: boolean; multipleDeals: boolean;
}
export interface Flow {
  id: string; name: string; status: "draft" | "published"; isActive: boolean; trigger: Trigger; steps: Step[]; settings: FlowSettings;
  ownerId: string | null; ownerName: string | null; triggerLabel: string; completed: number; pending: number; goalReached: number;
  createdAt: string; updatedAt: string; source?: string;
}
export interface FlowListItem {
  id: string; name: string; status: "draft" | "published"; isActive: boolean; triggerLabel: string; createdAt: string;
  ownerName: string | null; completed: number; pending: number; goalReached: number; stepsCount: number;
}

export interface Named { id: string; name: string }
export interface Lookups {
  forms: Named[]; fbForms: Named[]; surveys: Named[]; lists: Named[]; campaigns: Named[];
  pipelines: Array<Named & { stages: Array<Named & { type: string }> }>;
  users: Named[]; tags: string[]; statuses: string[]; customFields: Array<{ key: string; label: string }>;
}
export function useFlowLookups() {
  return useQuery<Lookups>({ queryKey: ["mkt-flow-lookups"], queryFn: () => api.get("/mkt/flows/lookups"), staleTime: 60_000 });
}

export const TRIGGER_LABELS: Record<TriggerType, string> = {
  form_submit: "Envío de formulario",
  fb_lead: "Formulario de Facebook (Lead Ads)",
  survey_response: "Respuesta de encuesta",
  list_added: "Añadido a una lista",
  tag_added: "Etiqueta añadida",
  email_click: "Clic en email de campaña",
};

export const STEP_META: Record<StepType, { label: string; icon: LucideIcon; disabled?: string }> = {
  send_email: { label: "Enviar email", icon: Mail },
  wait: { label: "Esperar", icon: Clock },
  add_tag: { label: "Añadir etiqueta", icon: Tag },
  remove_tag: { label: "Quitar etiqueta", icon: Tags },
  add_to_list: { label: "Añadir a lista", icon: ListPlus },
  create_deal: { label: "Crear oportunidad", icon: Briefcase },
  create_task: { label: "Crear tarea", icon: CheckSquare },
  notify: { label: "Notificar", icon: Bell },
  add_score: { label: "Sumar puntos", icon: Star },
  set_status: { label: "Cambiar estado", icon: Flag },
  set_owner: { label: "Cambiar propietario", icon: UserCog },
  condition: { label: "Condición", icon: GitBranch },
  send_whatsapp: { label: "Enviar WhatsApp", icon: MessageCircle, disabled: "Requiere WhatsApp Business API" },
};

export const ACTION_LABELS: Record<string, string> = {
  ...Object.fromEntries(Object.entries(STEP_META).map(([k, v]) => [k, v.label])),
  enter: "Entrada al flujo", finish: "Fin del flujo", goal: "Objetivo cumplido",
};

export const LOG_STATUS: Record<string, { label: string; color: string }> = {
  completed: { label: "Completado", color: "green" },
  scheduled: { label: "Programado", color: "blue" },
  skipped: { label: "Omitido", color: "gray" },
  failed: { label: "Fallido", color: "red" },
  stopped: { label: "Detenido", color: "amber" },
};
export const RUN_STATUS: Record<string, string> = {
  waiting: "En espera", running: "En ejecución", completed: "Completado", stopped: "Detenido", failed: "Fallido",
};

export const defaultSettings = (): FlowSettings => ({
  oncePerContact: true, singleFlow: false, goal: { type: "none", value: "" }, stopOnGoal: true, stopOnMeeting: false, multipleDeals: false,
});

export function defaultConfig(type: StepType, lk?: Lookups): StepConfig {
  const pipe = lk?.pipelines[0];
  switch (type) {
    case "send_email": return { subject: "", html: "<p>Hola {{nombre}},</p><p></p>" };
    case "wait": return { mode: "duration", amount: 1, unit: "days" };
    case "add_tag": case "remove_tag": return { tag: "" };
    case "add_to_list": return { listId: lk?.lists[0]?.id ?? "" };
    case "create_deal": return { pipelineId: pipe?.id ?? "", stageId: pipe?.stages.find((s) => s.type === "open")?.id ?? "", name: "{{nombre}} {{apellido}}", amount: 0, closingDays: 30 };
    case "create_task": return { subject: "Contactar a {{nombre}} {{apellido}}", dueDays: 1, assignTo: "contact_owner", priority: "Media" };
    case "notify": return { target: "contact_owner", inApp: true, email: "", message: "" };
    case "add_score": return { points: 10 };
    case "set_status": return { status: "" };
    case "set_owner": return { userId: lk?.users[0]?.id ?? "" };
    case "condition": return { check: "has_tag", key: "", value: "", onFalse: "end" };
    case "send_whatsapp": return { message: "" };
  }
}

export const newStepId = (): string => Math.random().toString(36).slice(2, 10);

/** Interruptor accesible. */
export function Toggle({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }): JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-primary" : "bg-line"}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

export function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string | undefined }): JSX.Element {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
      {hint && <div className="text-xs text-ink-muted mt-0.5">{hint}</div>}
    </div>
  );
}
